import { apiClient } from "@/api/modules/base.ts";
import { Route } from "@/api/modules/autoQ/checkout/route.ts";
import type { AxiosPromise } from "axios";
import type {
    CartCreateWithDataResponse,
    CartDocumentExistsResponse, CartGetCustomDocumentsResponse,
    CartGetDocumentsResponse, CartGetPredefinedDocumentsResponse,
    CartGetResponse, CartMailSizeResponse,
    CartRecipientsRequest, MailSettingsRequest, MailSettingsResponse
} from "@/api/modules/autoQ/checkout/types.ts";
import type {
    FileWithBase64Contents
} from "@/views/RestrictedArea/DocumentCheckout/CartOverviewPage/partials/CartCustomDocuments/types";

export const getCart = (): AxiosPromise<CartGetResponse> => apiClient.get(Route.CartGet);

export const getOrCreateCart = (): AxiosPromise<CartGetResponse> => apiClient.get(Route.CartGetOrCreate);

export const createCartWithData = (maillogItemId: number): AxiosPromise<CartCreateWithDataResponse> => {
    if (!Number.isInteger(maillogItemId) || maillogItemId <= 0) {
        throw new Error("maillogItemId must be a positive integer");
    }

    return apiClient.post(Route.CartCreateWithData, { maillogItemId });
};

export const deleteCart = (): AxiosPromise<void> => apiClient.delete(Route.CartDelete);

export const documentExists = (assetId: number): AxiosPromise<CartDocumentExistsResponse> => {
    if (!Number.isInteger(assetId) || assetId <= 0) {
        throw new Error("assetId must be a positive integer");
    }

    return apiClient.get(Route.DocumentExists, { params: { assetId } });
};

export const addDocuments = (assetIds: number[]): AxiosPromise<void> => {
    if (!assetIds?.length) {
        throw new Error("assetIds must be a non-empty array");
    }

    return apiClient.post(Route.DocumentsAdd, { assetIds });
};

export const getDocuments = (): AxiosPromise<CartGetDocumentsResponse> => apiClient.get(Route.DocumentsGet);

export const removeDocuments = (assetIds: number[]): AxiosPromise<void> => {
    if (!assetIds?.length) {
        throw new Error("assetIds must be a non-empty array");
    }

    return apiClient.delete(Route.DocumentsRemove, { data: { assetIds } });
};

export const syncDocuments = (assetIds: number[]): AxiosPromise<void> => {
    if (!assetIds?.length) {
        throw new Error("assetIds must be a non-empty array");
    }

    return apiClient.patch(Route.DocumentsSync, { assetIds });
};

export const getCustomDocuments = (): AxiosPromise<CartGetCustomDocumentsResponse> => apiClient.get(Route.CustomDocumentsGet);

export const uploadCustomDocuments = (fileObjects: FileWithBase64Contents[]): AxiosPromise<void> => apiClient.post(Route.CustomDocumentsUpload, fileObjects);

export const removeCustomDocuments = (assetIds: number[]): AxiosPromise<void> => apiClient.delete(Route.CustomDocumentsRemove, { data: { assetIds } });

export const getPredefinedDocuments = (): AxiosPromise<CartGetPredefinedDocumentsResponse> => apiClient.get(Route.PredefinedDocumentsGet);

export const syncPredefinedDocuments = (assetIds: number[]): AxiosPromise<void> => apiClient.patch(Route.PredefinedDocumentsSync, { assetIds });

export const addPredefinedDocuments = (assetIds: number[]): AxiosPromise<void> => apiClient.post(Route.PredefinedDocumentsAdd, { assetIds });

export const removePredefinedDocuments = (assetIds: number[]): AxiosPromise<void> => apiClient.delete(Route.PredefinedDocumentsRemove, { data: { assetIds } });

export const getMailSize = (): AxiosPromise<CartMailSizeResponse> => apiClient.get(Route.MailSizeGet);

export const getMailSettings = (): AxiosPromise<MailSettingsResponse> => apiClient.get(Route.MailSettingsGet);

export const setMailSettings = (payload: MailSettingsRequest): AxiosPromise<void> => apiClient.post(Route.MailSettingsSet, payload);

export const setCustomer = (customerId: number): AxiosPromise<void> => apiClient.post(Route.CustomerSet, { customerId });

export const submitCart = (): AxiosPromise<void> => apiClient.post(Route.CartSubmit);

export const setRecipients = (payload: CartRecipientsRequest): AxiosPromise<void> => apiClient.post(Route.RecipientsSet, payload);